import { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  belegungsabschnitte,
  berechneAbrechnung,
  formatEuro,
  tageBisFrist,
} from '@nkp/shared';
import type {
  AbrechnungsEingabe,
  AbrechnungsErgebnis,
  Einheit,
  Kostenposition,
  MietverhaeltnisAbrechnung,
  Schluessel,
} from '@nkp/shared';
import { Wizard } from '../components/Wizard';
import type { WizardSchritt } from '../components/Wizard';
import { SuccessAnimation } from '../components/SuccessAnimation';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Card, Kennzahl } from '../components/ui/Card';
import { EmptyState } from '../components/ui/EmptyState';
import { Input, Select } from '../components/ui/Input';
import { IconBelege, IconEntfernen, IconHinweis, IconInfo, IconPlus } from '../components/icons';
import {
  KOSTENARTEN,
  SCHLUESSEL_BEZEICHNUNG,
  SCHLUESSEL_ERLAEUTERUNG,
  findeKostenart,
  istUmlagefaehigImZeitraum,
} from '../lib/kostenarten';
import {
  beschreibeSaldo,
  formatDatum,
  formatQm,
  formatZahl,
  formatZeitraum,
  parseEuroEingabe,
  parseZahlEingabe,
} from '../lib/format';

/* ------------------------------------------------------------------ Entwurfsdaten */

interface EinheitEntwurf {
  id: string;
  bezeichnung: string;
  wohnflaeche: string;
}

interface MietverhaeltnisEntwurf {
  einheitId: string;
  mieter: string;
  beginn: string;
  ende: string;
  personen: string;
  vzNebenkosten: string;
  vzHeizkosten: string;
}

interface BelegEntwurf {
  id: string;
  kostenartCode: string;
  betrag: string;
  schluessel: Schluessel;
}

interface HeizkostenEntwurf {
  abrechnen: boolean;
  gesamtkosten: string;
  co2Kosten: string;
  verbrauchAnteil: string;
  co2Kennwert: string;
}

const SCHLUESSEL_LISTE: Schluessel[] = ['wohnflaeche', 'personen', 'einheiten', 'verbrauch'];

let laufendeNummer = 0;
function neueId(praefix: string): string {
  laufendeNummer += 1;
  return `${praefix}-${laufendeNummer}`;
}

/* ------------------------------------------------------------------ Seite */

export interface AbrechnungWizardProps {
  onFertig: () => void;
  onAbbruch: () => void;
}

export function AbrechnungWizard({ onFertig, onAbbruch }: AbrechnungWizardProps) {
  const [abgeschlossen, setAbgeschlossen] = useState(false);

  const [objekt, setObjekt] = useState({
    bezeichnung: 'Lindenstraße 14',
    strasse: 'Lindenstraße 14',
    plz: '45127',
    ort: 'Essen',
    von: '2025-01-01',
    bis: '2025-12-31',
  });

  const [einheiten, setEinheiten] = useState<EinheitEntwurf[]>([
    { id: neueId('einheit'), bezeichnung: 'Wohnung 1, EG links', wohnflaeche: '72' },
    { id: neueId('einheit'), bezeichnung: 'Wohnung 2, OG rechts', wohnflaeche: '58' },
  ]);

  const [mietverhaeltnis, setMietverhaeltnis] = useState<MietverhaeltnisEntwurf>(() => ({
    einheitId: '',
    mieter: '',
    beginn: '2025-01-01',
    ende: '',
    personen: '2',
    vzNebenkosten: '95,00',
    vzHeizkosten: '75,00',
  }));

  const [belege, setBelege] = useState<BelegEntwurf[]>([
    { id: neueId('beleg'), kostenartCode: 'grundsteuer', betrag: '860,00', schluessel: 'wohnflaeche' },
    {
      id: neueId('beleg'),
      kostenartCode: 'gebaeudereinigung',
      betrag: '1.240,00',
      schluessel: 'wohnflaeche',
    },
  ]);

  const [heizkosten, setHeizkosten] = useState<HeizkostenEntwurf>({
    abrechnen: true,
    gesamtkosten: '4.800,00',
    co2Kosten: '320,00',
    verbrauchAnteil: '70',
    co2Kennwert: '28',
  });

  // Zählerstände je Einheit (Anfang/Ende des Zeitraums). Verbrauch = Ende − Anfang.
  // Grundlage der verbrauchsabhängigen Verteilung; ohne Ablesung greift der Flächen-Fallback.
  const [ablesungen, setAblesungen] = useState<Record<string, { anfang: string; ende: string }>>({
    [einheiten[0]?.id ?? '']: { anfang: '12.000', ende: '14.820' },
    [einheiten[1]?.id ?? '']: { anfang: '8.000', ende: '9.950' },
  });

  function setzeAblesung(einheitId: string, feld: 'anfang' | 'ende', wert: string) {
    setAblesungen((a) => {
      const cur = a[einheitId] ?? { anfang: '', ende: '' };
      return { ...a, [einheitId]: { ...cur, [feld]: wert } };
    });
  }

  const ersteEinheitId = einheiten[0]?.id ?? '';
  const gewaehlteEinheitId = mietverhaeltnis.einheitId || ersteEinheitId;

  /* ----------------------------------------------------------- Berechnung */

  const berechnung = useMemo(
    () => baueUndBerechne({ objekt, einheiten, mietverhaeltnis: { ...mietverhaeltnis, einheitId: gewaehlteEinheitId }, belege, heizkosten, ablesungen }),
    [objekt, einheiten, mietverhaeltnis, gewaehlteEinheitId, belege, heizkosten, ablesungen],
  );

  /* ----------------------------------------------------------- Schritte */

  const schritte: WizardSchritt[] = [
    {
      id: 'objekt',
      titel: 'Objekt und Abrechnungszeitraum',
      kurz: 'Objekt',
      hinweis:
        'Der Abrechnungszeitraum umfasst in der Regel zwölf Monate. Die Abrechnungsfrist endet zwölf Monate nach seinem Ende (§ 556 Abs. 3 BGB).',
      inhalt: (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="Objektbezeichnung"
            value={objekt.bezeichnung}
            onChange={(e) => setObjekt({ ...objekt, bezeichnung: e.target.value })}
            className="sm:col-span-2"
          />
          <Input
            label="Straße und Hausnummer"
            value={objekt.strasse}
            onChange={(e) => setObjekt({ ...objekt, strasse: e.target.value })}
          />
          <div className="grid grid-cols-[7rem_1fr] gap-3">
            <Input
              label="PLZ"
              inputMode="numeric"
              value={objekt.plz}
              onChange={(e) => setObjekt({ ...objekt, plz: e.target.value })}
            />
            <Input
              label="Ort"
              value={objekt.ort}
              onChange={(e) => setObjekt({ ...objekt, ort: e.target.value })}
            />
          </div>
          <Input
            label="Abrechnungszeitraum, Beginn"
            type="date"
            value={objekt.von}
            onChange={(e) => setObjekt({ ...objekt, von: e.target.value })}
          />
          <Input
            label="Abrechnungszeitraum, Ende"
            type="date"
            value={objekt.bis}
            onChange={(e) => setObjekt({ ...objekt, bis: e.target.value })}
            hinweis={
              objekt.bis
                ? `Abrechnungsfrist bis ${formatDatum(fristEnde(objekt.bis))}`
                : undefined
            }
          />
        </div>
      ),
      pruefen: () => {
        const f: string[] = [];
        if (!objekt.bezeichnung.trim()) f.push('Objektbezeichnung angeben.');
        if (!objekt.von || !objekt.bis) f.push('Abrechnungszeitraum vollständig angeben.');
        else if (objekt.bis < objekt.von) f.push('Das Ende des Zeitraums liegt vor dem Beginn.');
        return f;
      },
    },

    {
      id: 'einheiten',
      titel: 'Einheiten',
      kurz: 'Einheiten',
      hinweis:
        'Die Wohnfläche ist der Standard-Umlageschlüssel. Leerstehende Einheiten bleiben in der Aufstellung; ihren Anteil trägt der Vermieter.',
      inhalt: (
        <div className="space-y-3">
          {einheiten.map((e, i) => (
            <div key={e.id} className="flex items-end gap-3">
              <Input
                label={`Bezeichnung ${i + 1}`}
                labelVerbergen={i > 0}
                className="min-w-0"
                value={e.bezeichnung}
                onChange={(ev) =>
                  setEinheiten(aendere(einheiten, i, { bezeichnung: ev.target.value }))
                }
              />
              <div className="w-32 shrink-0">
                <Input
                  label="Wohnfläche"
                  labelVerbergen={i > 0}
                  inputMode="decimal"
                  einheit="m²"
                  value={e.wohnflaeche}
                  onChange={(ev) =>
                    setEinheiten(aendere(einheiten, i, { wohnflaeche: ev.target.value }))
                  }
                />
              </div>
              <Button
                variante="dezent"
                groesse="md"
                aria-label={`Einheit ${e.bezeichnung} entfernen`}
                disabled={einheiten.length === 1}
                onClick={() => setEinheiten(einheiten.filter((x) => x.id !== e.id))}
                icon={<IconEntfernen groesse={18} />}
              />
            </div>
          ))}

          <Button
            variante="sekundaer"
            icon={<IconPlus groesse={18} />}
            onClick={() =>
              setEinheiten([
                ...einheiten,
                { id: neueId('einheit'), bezeichnung: `Wohnung ${einheiten.length + 1}`, wohnflaeche: '' },
              ])
            }
          >
            Einheit hinzufügen
          </Button>

          <p className="text-sm text-fg-muted">
            Gesamtwohnfläche:{' '}
            <span className="font-medium text-fg tabellenziffern">
              {formatQm(
                einheiten.reduce((s, e) => s + (parseZahlEingabe(e.wohnflaeche) ?? 0), 0),
              )}
            </span>
          </p>
        </div>
      ),
      pruefen: () => {
        const f: string[] = [];
        if (einheiten.length === 0) f.push('Mindestens eine Einheit anlegen.');
        einheiten.forEach((e, i) => {
          if (!e.bezeichnung.trim()) f.push(`Bezeichnung der Einheit ${i + 1} angeben.`);
          const flaeche = parseZahlEingabe(e.wohnflaeche);
          if (flaeche === null || flaeche <= 0) {
            f.push(`Wohnfläche der Einheit ${i + 1} angeben (größer als 0).`);
          }
        });
        return f;
      },
    },

    {
      id: 'mietverhaeltnis',
      titel: 'Mietverhältnis',
      kurz: 'Mietverhältnis',
      hinweis:
        'Bei Beginn oder Ende innerhalb des Zeitraums wird tagegenau abgegrenzt. Vorauszahlungen werden je berührtem Monat angesetzt.',
      inhalt: (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Select
            label="Einheit"
            value={gewaehlteEinheitId}
            onChange={(e) => setMietverhaeltnis({ ...mietverhaeltnis, einheitId: e.target.value })}
          >
            {einheiten.map((e) => (
              <option key={e.id} value={e.id}>
                {e.bezeichnung}
              </option>
            ))}
          </Select>
          <Input
            label="Mietpartei"
            placeholder="Nachname, Vorname"
            value={mietverhaeltnis.mieter}
            onChange={(e) => setMietverhaeltnis({ ...mietverhaeltnis, mieter: e.target.value })}
          />
          <Input
            label="Beginn des Mietverhältnisses"
            type="date"
            value={mietverhaeltnis.beginn}
            onChange={(e) => setMietverhaeltnis({ ...mietverhaeltnis, beginn: e.target.value })}
          />
          <Input
            label="Ende des Mietverhältnisses"
            type="date"
            value={mietverhaeltnis.ende}
            hinweis="Leer lassen, wenn das Mietverhältnis fortbesteht."
            onChange={(e) => setMietverhaeltnis({ ...mietverhaeltnis, ende: e.target.value })}
          />
          <Input
            label="Personenzahl"
            inputMode="numeric"
            value={mietverhaeltnis.personen}
            hinweis="Grundlage für den Umlageschlüssel Personenzahl."
            onChange={(e) => setMietverhaeltnis({ ...mietverhaeltnis, personen: e.target.value })}
          />
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Vorauszahlung Nebenkosten"
              inputMode="decimal"
              einheit="€"
              value={mietverhaeltnis.vzNebenkosten}
              onChange={(e) =>
                setMietverhaeltnis({ ...mietverhaeltnis, vzNebenkosten: e.target.value })
              }
            />
            <Input
              label="Vorauszahlung Heizkosten"
              inputMode="decimal"
              einheit="€"
              value={mietverhaeltnis.vzHeizkosten}
              onChange={(e) =>
                setMietverhaeltnis({ ...mietverhaeltnis, vzHeizkosten: e.target.value })
              }
            />
          </div>
        </div>
      ),
      pruefen: () => {
        const f: string[] = [];
        if (!mietverhaeltnis.mieter.trim()) f.push('Mietpartei angeben.');
        if (!mietverhaeltnis.beginn) f.push('Beginn des Mietverhältnisses angeben.');
        if (mietverhaeltnis.ende && mietverhaeltnis.ende < mietverhaeltnis.beginn) {
          f.push('Das Ende des Mietverhältnisses liegt vor dem Beginn.');
        }
        const personen = parseZahlEingabe(mietverhaeltnis.personen);
        if (personen === null || personen < 1) f.push('Personenzahl angeben (mindestens 1).');
        if (parseEuroEingabe(mietverhaeltnis.vzNebenkosten) === null) {
          f.push('Vorauszahlung Nebenkosten als Betrag angeben.');
        }
        if (parseEuroEingabe(mietverhaeltnis.vzHeizkosten) === null) {
          f.push('Vorauszahlung Heizkosten als Betrag angeben.');
        }
        return f;
      },
    },

    {
      id: 'belege',
      titel: 'Belege und Umlageschlüssel',
      kurz: 'Belege',
      hinweis:
        'Je Kostenart werden Gesamtbetrag und Umlageschlüssel erfasst. Nicht umlagefähige Positionen wie Instandhaltung und Verwaltung bleiben außen vor.',
      inhalt: (
        <div className="space-y-3">
          {belege.length === 0 ? (
            <EmptyState
              icon={<IconBelege groesse={22} />}
              titel="Keine Belege erfasst"
              beschreibung="Erfassen Sie die Rechnungen des Abrechnungszeitraums je Kostenart."
            />
          ) : (
            belege.map((b, i) => {
              const art = findeKostenart(b.kostenartCode);
              const gesperrt = art ? !istUmlagefaehigImZeitraum(art, objekt.bis) : false;
              return (
                <div
                  key={b.id}
                  className="rounded-lg border border-border bg-surface-2 p-3"
                >
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_9rem_auto]">
                    <Select
                      label="Kostenart"
                      labelVerbergen={i > 0}
                      value={b.kostenartCode}
                      onChange={(e) => {
                        const neu = findeKostenart(e.target.value);
                        setBelege(
                          aendere(belege, i, {
                            kostenartCode: e.target.value,
                            schluessel: neu?.standardSchluessel ?? b.schluessel,
                          }),
                        );
                      }}
                    >
                      {KOSTENARTEN.map((k) => (
                        <option key={k.code} value={k.code}>
                          {k.nummer}. {k.bezeichnung}
                        </option>
                      ))}
                    </Select>
                    <Input
                      label="Betrag"
                      labelVerbergen={i > 0}
                      inputMode="decimal"
                      einheit="€"
                      value={b.betrag}
                      onChange={(e) => setBelege(aendere(belege, i, { betrag: e.target.value }))}
                    />
                    <div className="flex items-end">
                      <Button
                        variante="dezent"
                        aria-label="Beleg entfernen"
                        onClick={() => setBelege(belege.filter((x) => x.id !== b.id))}
                        icon={<IconEntfernen groesse={18} />}
                      />
                    </div>
                  </div>

                  <div className="mt-3">
                    <Select
                      label="Umlageschlüssel"
                      labelVerbergen={i > 0}
                      value={b.schluessel}
                      hinweis={SCHLUESSEL_ERLAEUTERUNG[b.schluessel]}
                      onChange={(e) =>
                        setBelege(aendere(belege, i, { schluessel: e.target.value as Schluessel }))
                      }
                    >
                      {SCHLUESSEL_LISTE.map((s) => (
                        <option key={s} value={s}>
                          {SCHLUESSEL_BEZEICHNUNG[s]}
                        </option>
                      ))}
                    </Select>
                  </div>

                  {gesperrt && art && (
                    <p className="mt-2 flex items-start gap-2 text-xs text-warning-soft-fg">
                      <span className="mt-0.5 shrink-0">
                        <IconHinweis groesse={14} />
                      </span>
                      {art.hinweis}
                    </p>
                  )}
                </div>
              );
            })
          )}

          <Button
            variante="sekundaer"
            icon={<IconPlus groesse={18} />}
            onClick={() =>
              setBelege([
                ...belege,
                {
                  id: neueId('beleg'),
                  kostenartCode: 'hausmeister',
                  betrag: '',
                  schluessel: 'wohnflaeche',
                },
              ])
            }
          >
            Beleg hinzufügen
          </Button>
        </div>
      ),
      pruefen: () => {
        const f: string[] = [];
        if (belege.length === 0) f.push('Mindestens einen Beleg erfassen.');
        belege.forEach((b, i) => {
          if (parseEuroEingabe(b.betrag) === null) {
            f.push(`Betrag der Position ${i + 1} als Zahl angeben.`);
          }
        });
        return f;
      },
    },

    {
      id: 'zaehler',
      titel: 'Zählerstände zum Stichtag',
      kurz: 'Zähler',
      hinweis:
        'Verbrauch = Endstand − Anfangsstand je Einheit. Diese Werte verteilen die verbrauchsabhängigen Positionen (Heizung, Warmwasser, ggf. Wasser). Ohne Ablesung wird ersatzweise nach Fläche und Dauer geschätzt — das ist bei fehlenden Verbrauchsdaten angreifbar (§ 12 HeizkostenV, 15 % Kürzungsrecht).',
      inhalt: (
        <div className="space-y-3">
          {einheiten.map((e) => {
            const a = ablesungen[e.id] ?? { anfang: '', ende: '' };
            const anfang = parseZahlEingabe(a.anfang);
            const ende = parseZahlEingabe(a.ende);
            const verbrauch = anfang !== null && ende !== null ? ende - anfang : null;
            return (
              <div key={e.id} className="rounded-lg border border-border bg-surface-2 p-3">
                <p className="mb-2 text-sm font-medium text-fg">{e.bezeichnung || e.id}</p>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <Input
                    label="Anfangsstand"
                    inputMode="decimal"
                    value={a.anfang}
                    onChange={(ev) => setzeAblesung(e.id, 'anfang', ev.target.value)}
                  />
                  <Input
                    label="Endstand"
                    inputMode="decimal"
                    value={a.ende}
                    onChange={(ev) => setzeAblesung(e.id, 'ende', ev.target.value)}
                  />
                  <div className="flex flex-col justify-end">
                    <span className="mb-1.5 text-sm font-medium text-fg">Verbrauch</span>
                    <span
                      className={`tabellenziffern text-sm font-semibold ${
                        verbrauch !== null && verbrauch < 0 ? 'text-danger' : 'text-fg'
                      }`}
                    >
                      {verbrauch === null ? '—' : formatZahl(verbrauch)}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ),
      pruefen: () => {
        const f: string[] = [];
        einheiten.forEach((e) => {
          const a = ablesungen[e.id];
          if (!a) return;
          const anfang = parseZahlEingabe(a.anfang);
          const ende = parseZahlEingabe(a.ende);
          if (anfang !== null && ende !== null && ende < anfang) {
            f.push(`Endstand liegt unter dem Anfangsstand bei ${e.bezeichnung || e.id}.`);
          }
        });
        return f;
      },
    },

    {
      id: 'heizkosten',
      titel: 'Heizkosten und CO₂-Anteil',
      kurz: 'Heizkosten',
      hinweis:
        'Nach HeizkostenV sind 50 bis 70 Prozent der Heiz- und Warmwasserkosten verbrauchsabhängig zu verteilen. Der Vermieteranteil an den CO₂-Kosten wird vorab abgezogen und gesondert ausgewiesen.',
      inhalt: (
        <div className="space-y-4">
          <label className="flex items-start gap-3 rounded-lg border border-border bg-surface-2 p-3">
            <input
              type="checkbox"
              checked={heizkosten.abrechnen}
              onChange={(e) => setHeizkosten({ ...heizkosten, abrechnen: e.target.checked })}
              className="mt-0.5 h-4 w-4 accent-accent"
            />
            <span className="text-sm">
              <span className="font-medium text-fg">Heizkosten in dieser Abrechnung erfassen</span>
              <span className="mt-0.5 block text-fg-muted">
                Andernfalls werden ausschließlich die kalten Betriebskosten abgerechnet.
              </span>
            </span>
          </label>

          {heizkosten.abrechnen && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input
                label="Gesamtkosten Heizung und Warmwasser"
                inputMode="decimal"
                einheit="€"
                value={heizkosten.gesamtkosten}
                onChange={(e) => setHeizkosten({ ...heizkosten, gesamtkosten: e.target.value })}
              />
              <Input
                label="Darin enthaltene CO₂-Kosten"
                inputMode="decimal"
                einheit="€"
                value={heizkosten.co2Kosten}
                onChange={(e) => setHeizkosten({ ...heizkosten, co2Kosten: e.target.value })}
              />
              <Input
                label="Verbrauchsabhängiger Anteil"
                inputMode="numeric"
                einheit="%"
                value={heizkosten.verbrauchAnteil}
                hinweis="Zulässig sind 50 bis 70 Prozent; der Rest wird nach Wohnfläche verteilt."
                onChange={(e) => setHeizkosten({ ...heizkosten, verbrauchAnteil: e.target.value })}
              />
              <Input
                label="CO₂-Kennwert des Gebäudes"
                inputMode="decimal"
                einheit="kg/m²a"
                value={heizkosten.co2Kennwert}
                hinweis="Grundlage des Stufenmodells für Abrechnungsjahre bis 2027."
                onChange={(e) => setHeizkosten({ ...heizkosten, co2Kennwert: e.target.value })}
              />
            </div>
          )}
        </div>
      ),
      pruefen: () => {
        if (!heizkosten.abrechnen) return [];
        const f: string[] = [];
        if (parseEuroEingabe(heizkosten.gesamtkosten) === null) {
          f.push('Gesamtkosten Heizung und Warmwasser angeben.');
        }
        if (parseEuroEingabe(heizkosten.co2Kosten) === null) {
          f.push('Enthaltene CO₂-Kosten angeben (0, wenn keine anfallen).');
        }
        const anteil = parseZahlEingabe(heizkosten.verbrauchAnteil);
        if (anteil === null || anteil < 50 || anteil > 70) {
          f.push('Verbrauchsabhängiger Anteil muss zwischen 50 und 70 Prozent liegen.');
        }
        const jahr = Number(objekt.bis.slice(0, 4));
        if (jahr < 2028 && parseZahlEingabe(heizkosten.co2Kennwert) === null) {
          f.push('CO₂-Kennwert des Gebäudes angeben (Stufenmodell bis Abrechnungsjahr 2027).');
        }
        return f;
      },
    },

    {
      id: 'ergebnis',
      titel: 'Ergebnis',
      kurz: 'Ergebnis',
      hinweis:
        'Die Aufstellung enthält Gesamtkosten, Umlageschlüssel, Anteilsberechnung und Abzug der Vorauszahlungen.',
      inhalt: <ErgebnisAnsicht berechnung={berechnung} objektName={objekt.bezeichnung} />,
      pruefen: () =>
        berechnung.fehler ? ['Die Abrechnung kann mit den aktuellen Angaben nicht erstellt werden.'] : [],
    },
  ];

  if (abgeschlossen) {
    return (
      <div className="mx-auto max-w-xl py-8">
        <SuccessAnimation
          titel="Abrechnung abgeschlossen"
          beschreibung={
            <>
              Die Abrechnung für {objekt.bezeichnung} ({formatZeitraum(objekt.von, objekt.bis)}) ist
              erstellt und kann geprüft, festgeschrieben und versendet werden.
            </>
          }
          aktion={
            <>
              <Button onClick={onFertig}>Zur Übersicht</Button>
              <Button variante="sekundaer" onClick={() => setAbgeschlossen(false)}>
                Angaben bearbeiten
              </Button>
            </>
          }
        />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl">
      <Wizard
        schritte={schritte}
        abschlussLabel="Abrechnung abschließen"
        onAbschluss={() => setAbgeschlossen(true)}
        onAbbruch={onAbbruch}
        abbruchLabel="Abbrechen"
      />
    </div>
  );
}

export default AbrechnungWizard;

/* ------------------------------------------------------------------ Ergebnis */

interface BerechnungsStand {
  ergebnis: AbrechnungsErgebnis | null;
  fehler: string | null;
}

function ErgebnisAnsicht({
  berechnung,
  objektName,
}: {
  berechnung: BerechnungsStand;
  objektName: string;
}) {
  if (berechnung.fehler || !berechnung.ergebnis) {
    return (
      <div className="flex gap-3 rounded-lg border border-danger bg-danger-soft px-3 py-3 text-sm text-danger-soft-fg">
        <span className="mt-0.5 shrink-0">
          <IconHinweis groesse={18} />
        </span>
        <div>
          <p className="font-medium">Abrechnung nicht berechenbar</p>
          <p className="mt-1">{berechnung.fehler}</p>
        </div>
      </div>
    );
  }

  const e = berechnung.ergebnis;
  const gesamtkosten = Object.values(e.gesamtkostenJeKostenart).reduce((s, c) => s + c, 0);
  const restTage = Math.round(tageBisFrist(e.zeitraum.bis, heuteIso()));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 rounded-lg border border-border bg-surface-2 p-4 sm:grid-cols-3">
        <Kennzahl bezeichnung="Gesamtkosten" wert={formatEuro(gesamtkosten)} zusatz={objektName} />
        <Kennzahl
          bezeichnung="Anteil Vermieter"
          wert={formatEuro(e.vermieterAnteilCent)}
          zusatz="Leerstand und Eigennutzung"
        />
        <Kennzahl
          bezeichnung="Abrechnungsfrist"
          wert={formatDatum(e.abrechnungsfristBis)}
          zusatz={restTage >= 0 ? `noch ${restTage} Tage` : 'Frist verstrichen'}
          ton={restTage >= 0 ? 'neutral' : 'warnung'}
        />
      </div>

      <Card titel="Gesamtkosten je Kostenart" ohnePolster>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-fg-subtle">
                <th className="px-4 py-2 font-medium">Kostenart</th>
                <th className="px-4 py-2 text-right font-medium">Betrag</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(e.gesamtkostenJeKostenart).map(([code, betrag]) => (
                <tr key={code} className="border-b border-border last:border-0">
                  <td className="px-4 py-2 text-fg">{bezeichneKostenart(code)}</td>
                  <td className="px-4 py-2 text-right tabellenziffern text-fg">
                    {formatEuro(betrag)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {e.mieter.map((m) => {
        const saldo = beschreibeSaldo(m.saldoCent);
        return (
          <Card key={m.mietverhaeltnisId} titel="Ergebnis des Mietverhältnisses">
            <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Zeile bezeichnung="Anteil Betriebskosten" wert={formatEuro(m.kostenNkCent)} />
              <Zeile bezeichnung="Anteil Heizkosten" wert={formatEuro(m.kostenHeizungCent)} />
              <Zeile bezeichnung="Vorauszahlungen" wert={formatEuro(m.vorauszahlungCent)} />
              <Zeile bezeichnung="Tage im Zeitraum" wert={String(m.tageImZeitraum)} />
            </dl>
            <div className="mt-4 flex items-center justify-between rounded-lg bg-surface-2 px-3 py-3">
              <span className="text-sm font-medium text-fg">{saldo.bezeichnung}</span>
              <Badge ton={saldo.ton === 'erfolg' ? 'erfolg' : saldo.ton === 'warnung' ? 'warnung' : 'neutral'}>
                <span className="tabellenziffern">{saldo.betrag}</span>
              </Badge>
            </div>
          </Card>
        );
      })}

      {e.co2 && (
        <Card
          titel="CO₂-Kostenaufteilung"
          beschreibung={
            e.co2.modell === 'stufen_10'
              ? `Stufenmodell, Stufe ${e.co2.stufe} bei ${e.co2.kennwert} kg CO₂ je m² und Jahr`
              : 'Feste Aufteilung 50/50 ab Abrechnungsjahr 2028'
          }
        >
          <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Zeile
              bezeichnung={`Anteil Vermieter (${e.co2.vermieterProzent} %)`}
              wert={formatEuro(e.co2.vermieterAnteilCent)}
            />
            <Zeile
              bezeichnung={`Anteil Mieterschaft (${e.co2.mieterProzent} %)`}
              wert={formatEuro(e.co2.mieterAnteilCent)}
            />
          </dl>
        </Card>
      )}

      {e.warnungen.length > 0 && (
        <div className="rounded-lg border border-warning bg-warning-soft px-3 py-3">
          <p className="flex items-center gap-2 text-sm font-medium text-warning-soft-fg">
            <IconHinweis groesse={16} />
            Hinweise zur Abrechnung
          </p>
          <ul className="mt-2 list-disc space-y-1 pl-6 text-sm text-warning-soft-fg">
            {e.warnungen.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        </div>
      )}

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="flex gap-3 rounded-lg border border-border bg-surface-2 px-3 py-3 text-sm text-fg-muted"
      >
        <span className="mt-0.5 shrink-0 text-accent">
          <IconInfo groesse={18} />
        </span>
        <p>
          Die Aufstellung weist Gesamtkosten, Umlageschlüssel, Anteilsberechnung und Abzug der
          Vorauszahlungen aus. Diese vier Angaben übernimmt das Abrechnungs-PDF unverändert.
        </p>
      </motion.div>
    </div>
  );
}

function Zeile({ bezeichnung, wert }: { bezeichnung: string; wert: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-border pb-2 last:border-0">
      <dt className="text-sm text-fg-muted">{bezeichnung}</dt>
      <dd className="text-sm font-medium tabellenziffern text-fg">{wert}</dd>
    </div>
  );
}

/* ------------------------------------------------------------------ Hilfen */

function aendere<T>(liste: T[], index: number, patch: Partial<T>): T[] {
  return liste.map((eintrag, i) => (i === index ? { ...eintrag, ...patch } : eintrag));
}

function heuteIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function fristEnde(bis: string): string {
  const [y, m, d] = bis.split('-').map(Number);
  if (!y || !m || !d) return bis;
  return new Date(Date.UTC(y + 1, m - 1, d)).toISOString().slice(0, 10);
}

function bezeichneKostenart(code: string): string {
  if (code === 'heizung_verbrauch') return 'Heizkosten, verbrauchsabhängiger Anteil';
  if (code === 'heizung_grundkosten') return 'Heizkosten, Grundkosten nach Wohnfläche';
  return findeKostenart(code)?.bezeichnung ?? code;
}

/**
 * Baut aus dem Entwurf die Eingabe für den Rechenkern und führt die Abrechnung aus.
 * Es findet kein Netzzugriff statt — der Rechenkern ist eine reine Funktion.
 */
function baueUndBerechne(args: {
  objekt: { bezeichnung: string; von: string; bis: string };
  einheiten: EinheitEntwurf[];
  mietverhaeltnis: MietverhaeltnisEntwurf;
  belege: BelegEntwurf[];
  heizkosten: HeizkostenEntwurf;
  ablesungen: Record<string, { anfang: string; ende: string }>;
}): BerechnungsStand {
  const { objekt, einheiten, mietverhaeltnis, belege, heizkosten, ablesungen } = args;

  try {
    const zeitraum = { von: objekt.von, bis: objekt.bis };

    const kernEinheiten: Einheit[] = einheiten.map((e) => ({
      id: e.id,
      bezeichnung: e.bezeichnung || e.id,
      wohnflaecheQm: parseZahlEingabe(e.wohnflaeche) ?? 0,
    }));

    const kernMietverhaeltnisse: MietverhaeltnisAbrechnung[] = [
      {
        id: 'mv-entwurf',
        einheitId: mietverhaeltnis.einheitId || (kernEinheiten[0]?.id ?? ''),
        beginn: mietverhaeltnis.beginn,
        ende: mietverhaeltnis.ende ? mietverhaeltnis.ende : null,
        personenzahl: parseZahlEingabe(mietverhaeltnis.personen) ?? 1,
        vzNebenkostenCentProMonat: parseEuroEingabe(mietverhaeltnis.vzNebenkosten) ?? 0,
        vzHeizkostenCentProMonat: parseEuroEingabe(mietverhaeltnis.vzHeizkosten) ?? 0,
      },
    ];

    // Belegungsabschnitte werden für die Zuordnung der Verbrauchswerte benötigt.
    const abschnitte = belegungsabschnitte(zeitraum, kernEinheiten, kernMietverhaeltnisse);
    const flaecheJeEinheit = new Map(kernEinheiten.map((e) => [e.id, e.wohnflaecheQm]));

    // Verbrauch je Einheit aus den Zählerständen (Ende − Anfang), sofern erfasst.
    const verbrauchJeEinheit = new Map<string, number>();
    for (const e of kernEinheiten) {
      const a = ablesungen[e.id];
      const anfang = a ? parseZahlEingabe(a.anfang) : null;
      const ende = a ? parseZahlEingabe(a.ende) : null;
      if (anfang !== null && ende !== null && ende >= anfang) {
        verbrauchJeEinheit.set(e.id, ende - anfang);
      }
    }

    // Gesamttage je Einheit über alle Abschnitte — zur tagegenauen Aufteilung des Verbrauchs.
    const tageJeEinheit = new Map<string, number>();
    for (const a of abschnitte) {
      tageJeEinheit.set(a.einheitId, (tageJeEinheit.get(a.einheitId) ?? 0) + a.tage);
    }

    const verbrauchJeAbschnitt: Record<string, number> = {};
    for (const a of abschnitte) {
      const verbrauchEinheit = verbrauchJeEinheit.get(a.einheitId);
      if (verbrauchEinheit !== undefined) {
        // Gemessener Verbrauch, tagegenau auf die Belegungsabschnitte der Einheit verteilt.
        const tage = tageJeEinheit.get(a.einheitId) ?? a.tage;
        verbrauchJeAbschnitt[a.id] = tage > 0 ? Math.round((verbrauchEinheit * a.tage) / tage) : 0;
      } else {
        // Fallback ohne Ablesung: proportional zu Fläche und Dauer (angreifbar, s. Hinweis).
        verbrauchJeAbschnitt[a.id] = Math.round((flaecheJeEinheit.get(a.einheitId) ?? 0) * a.tage * 0.1);
      }
    }

    const kostenpositionen: Kostenposition[] = belege.map((b) => {
      const art = findeKostenart(b.kostenartCode);
      return {
        kostenartCode: b.kostenartCode,
        bezeichnung: art?.bezeichnung ?? b.kostenartCode,
        schluessel: b.schluessel,
        betragCent: parseEuroEingabe(b.betrag) ?? 0,
        verbrauchJeAbschnitt: b.schluessel === 'verbrauch' ? verbrauchJeAbschnitt : undefined,
        erlaeuterung: SCHLUESSEL_ERLAEUTERUNG[b.schluessel],
      };
    });

    const eingabe: AbrechnungsEingabe = {
      zeitraum,
      einheiten: kernEinheiten,
      mietverhaeltnisse: kernMietverhaeltnisse,
      kostenpositionen,
      heizkosten: heizkosten.abrechnen
        ? {
            gesamtkostenCent: parseEuroEingabe(heizkosten.gesamtkosten) ?? 0,
            co2KostenCent: parseEuroEingabe(heizkosten.co2Kosten) ?? 0,
            verbrauchAnteilProzent: parseZahlEingabe(heizkosten.verbrauchAnteil) ?? 70,
            verbrauchJeAbschnitt,
            co2Kennwert: parseZahlEingabe(heizkosten.co2Kennwert) ?? undefined,
          }
        : undefined,
    };

    return { ergebnis: berechneAbrechnung(eingabe), fehler: null };
  } catch (fehler) {
    return {
      ergebnis: null,
      fehler: fehler instanceof Error ? fehler.message : 'Unbekannter Fehler in der Berechnung.',
    };
  }
}
